import { Box, Button, Link, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { getAuth, signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import FirestoreUser from '../../../../types/FirestoreUser'
import useIsMine from '../../../../hooks/useIsMine'
import useImpersonationSession from '../../../../hooks/useImpersonationSession'
import {
  PersonalForm,
  DiagnosisDetails,
  ChangePasswordButton,
  WelcomeModal,
} from './components'
import {
  usePersonalForm,
  useDiagnosisManagement,
  usePasswordActions,
  usePersonalTabValidation,
  useProfileSubmit,
} from './hooks'

interface Props {
  uid: string
  firestoreUser: FirestoreUser
  canEditLockedMedicalDates?: boolean
}

const PersonalTab = ({
  uid,
  firestoreUser,
  canEditLockedMedicalDates = false,
}: Props) => {
  const isMine = useIsMine(uid)
  const navigate = useNavigate()
  const session = useImpersonationSession()

  const {
    isOpen: isWelcomeModalOpen,
    onOpen: onWelcomeModalOpen,
    onClose: onWelcomeModalClose,
  } = useDisclosure()

  const {
    profile,
    setProfile,
    isSaved,
    setIsSaved,
    loading,
    setLoading,
    units,
    prevDiagnosisRef,
    extraInfoRequired,
    areDateFieldsDisabled,
    areFirstSymptomsFieldsDisabled,
    update,
    updateUnitSystem,
    handleError,
    toast,
    isBirthDateLocked,
    isDiagnosisDateLocked,
    isFirstSymptomsDateLocked,
  } = usePersonalForm({
    uid,
    firestoreUser,
    isImpersonating: session.isImpersonating,
    canEditLockedMedicalDates,
  })

  const { files, setFiles, uploadDiagnosisDocuments } =
    useDiagnosisManagement(uid)
  const { handleChangePassword } = usePasswordActions()
  const { validateProfile } = usePersonalTabValidation(toast)
  const { submitProfile } = useProfileSubmit({
    uid,
    firestoreUser,
    toast,
    prevDiagnosisRef,
    setProfile,
    setIsSaved,
    setLoading,
    handleError,
    uploadDiagnosisDocuments,
    files,
  })

  // Clear diagnosisDate when switching to 'als-probable'
  useEffect(() => {
    if (profile.diagnosis === 'als-probable' && profile.diagnosisDate) {
      setProfile((current) => {
        const { diagnosisDate, ...rest } = current
        return rest as typeof current
      })
    }
  }, [profile.diagnosis, profile.diagnosisDate, setProfile])

  const submit = async () => {
    // Trim first name before validation
    if (profile.firstName) {
      profile.firstName = profile.firstName.trim()
    }

    const validationResult = validateProfile({
      profile,
      isMine,
      extraInfoRequired,
      areDateFieldsDisabled,
      areFirstSymptomsFieldsDisabled,
    })

    if (!validationResult.isValid) {
      if (validationResult.errorMessage) {
        alert(validationResult.errorMessage)
      }
      return
    }

    const result = await submitProfile(profile)
    if (result.isFirstCompletion) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      onWelcomeModalOpen()
    }
  }

  return (
    <>
      <WelcomeModal isOpen={isWelcomeModalOpen} onClose={onWelcomeModalClose} />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <Stack spacing={5}>
          <PersonalForm
            profile={profile}
            units={units}
            extraInfoRequired={extraInfoRequired}
            areDateFieldsDisabled={areDateFieldsDisabled}
            areFirstSymptomsFieldsDisabled={areFirstSymptomsFieldsDisabled}
            onUpdate={update}
            onUpdateUnitSystem={updateUnitSystem}
            isMine={isMine}
            isSaved={isSaved}
            isBirthDateLocked={isBirthDateLocked}
            isDiagnosisDateLocked={isDiagnosisDateLocked}
            isFirstSymptomsDateLocked={isFirstSymptomsDateLocked}
          />

          <DiagnosisDetails
            profile={profile}
            files={files}
            onFilesChange={setFiles}
          />

          {isMine && !session.isImpersonating && (
            <Text>
              Under penalty of perjury, I acknowledge that I have read and agree
              to the{' '}
              <Link href='/terms' isExternal color='purple.600'>
                Terms and Conditions
              </Link>{' '}
              . I acknowledge that all information contained in the Registry or
              about any member is confidential. I acknowledge that I am not
              employed by nor am a contractor for a governmental nor
              pharmaceutical organization, either directly or indirectly,
              including but not limited to the FDA, FTC, CDC or NIH. This
              registry is for educational purposes only, and is not intended to
              replace medical prescriptions nor advice from your doctors.
            </Text>
          )}

          <Box>
            <Button
              type='submit'
              disabled={loading}
              isLoading={loading}
              size='lg'
            >
              Save
            </Button>
          </Box>

          {isMine && !session.isImpersonating && (
            <>
              <ChangePasswordButton onChangePassword={handleChangePassword} />
              <Box>
                <Button
                  colorScheme='red'
                  variant='link'
                  onClick={() => {
                    if (window.confirm('Sign out?')) {
                      signOut(getAuth()).then(() => navigate('/'))
                    }
                  }}
                >
                  Sign Out
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </form>
    </>
  )
}

export default PersonalTab
